// Top-up pack catalog for the AI credit top-up flow (#09).
// See plans/saas-billing/09-ai-credit-top-up.md.
//
// Four fixed pack sizes (1k / 5k / 10k / 25k) priced per spec with tiered
// discounts (0 / 10 / 20 / 30%) applied to the per-credit base rate. Each pack
// lists pricing for every supported currency (USD/NGN/GHS/KES/ZAR/GBP); EUR is
// intentionally out of scope per the spec (EU markets not commercially open).
//
// Pricing rationale (matches spec exactly):
//   USD base = $0.010/credit (1k = $10.00 = 1000 minor)
//   NGN base = ₦4.50/credit (1k = ₦4500 = 450000 minor)
//   GHS base = GH₵0.11/credit (1k = GH₵110.00 = 11000 minor)
//   KES base = KSh 1.30/credit (1k = KSh 1300 = 130000 minor)
//   ZAR base = R 0.18/credit (1k = R 180 = 18000 minor)
//   GBP base = £0.008/credit (1k = £8.00 = 800 minor)
// Tier discounts: 5k -10%, 10k -20%, 25k -30%. Final minor values rounded.

import { TopUpPack } from '../../../models/billing';

export const TOP_UP_PACKS: TopUpPack[] = [
  {
    id: 'topup_1k',
    displayName: '1,000 credits',
    credits: 1000,
    pricing: [
      { currency: 'USD', countryCode: 'US', amountMinor: 1000, stripePriceId: null },
      { currency: 'NGN', countryCode: 'NG', amountMinor: 450000, stripePriceId: null },
      { currency: 'GHS', countryCode: 'GH', amountMinor: 11000, stripePriceId: null },
      { currency: 'KES', countryCode: 'KE', amountMinor: 130000, stripePriceId: null },
      { currency: 'ZAR', countryCode: 'ZA', amountMinor: 18000, stripePriceId: null },
      { currency: 'GBP', countryCode: 'GB', amountMinor: 800, stripePriceId: null }
    ]
  },
  {
    id: 'topup_5k',
    displayName: '5,000 credits',
    credits: 5000,
    pricing: [
      { currency: 'USD', countryCode: 'US', amountMinor: 4500, stripePriceId: null },
      { currency: 'NGN', countryCode: 'NG', amountMinor: 2000000, stripePriceId: null },
      { currency: 'GHS', countryCode: 'GH', amountMinor: 50000, stripePriceId: null },
      { currency: 'KES', countryCode: 'KE', amountMinor: 580000, stripePriceId: null },
      { currency: 'ZAR', countryCode: 'ZA', amountMinor: 80000, stripePriceId: null },
      { currency: 'GBP', countryCode: 'GB', amountMinor: 3600, stripePriceId: null }
    ]
  },
  {
    id: 'topup_10k',
    displayName: '10,000 credits',
    credits: 10000,
    pricing: [
      { currency: 'USD', countryCode: 'US', amountMinor: 8000, stripePriceId: null },
      { currency: 'NGN', countryCode: 'NG', amountMinor: 3600000, stripePriceId: null },
      { currency: 'GHS', countryCode: 'GH', amountMinor: 88000, stripePriceId: null },
      { currency: 'KES', countryCode: 'KE', amountMinor: 1040000, stripePriceId: null },
      { currency: 'ZAR', countryCode: 'ZA', amountMinor: 144000, stripePriceId: null },
      { currency: 'GBP', countryCode: 'GB', amountMinor: 6400, stripePriceId: null }
    ]
  },
  {
    id: 'topup_25k',
    displayName: '25,000 credits',
    credits: 25000,
    pricing: [
      { currency: 'USD', countryCode: 'US', amountMinor: 17500, stripePriceId: null },
      { currency: 'NGN', countryCode: 'NG', amountMinor: 7800000, stripePriceId: null },
      { currency: 'GHS', countryCode: 'GH', amountMinor: 192500, stripePriceId: null },
      { currency: 'KES', countryCode: 'KE', amountMinor: 2275000, stripePriceId: null },
      { currency: 'ZAR', countryCode: 'ZA', amountMinor: 315000, stripePriceId: null },
      { currency: 'GBP', countryCode: 'GB', amountMinor: 14000, stripePriceId: null }
    ]
  }
];

/**
 * Tier discount lookup so the UI can render the "Save X%" badge without
 * recomputing from base prices. Indexed by pack id.
 */
export const TOP_UP_PACK_DISCOUNT_PERCENT: Record<string, number> = {
  topup_1k: 0,
  topup_5k: 10,
  topup_10k: 20,
  topup_25k: 30
};

/**
 * Per-spec badges. Surfaced by the pack picker UI; declared here so the
 * fixture, mock service, and component agree on which packs carry which
 * label.
 */
export const TOP_UP_PACK_BADGE: Record<string, 'most_popular' | 'best_value' | null> = {
  topup_1k: null,
  topup_5k: 'most_popular',
  topup_10k: null,
  topup_25k: 'best_value'
};
